// ============================================================
// guiasPage.js — Página "Guías" (listado)
// ------------------------------------------------------------
// - Filtra el grid por etiqueta de categoría
// - Expone window.Vimo.guides para que el backend inyecte
//   guías reales:
//     window.Vimo.guides.setItems(items)
//     window.Vimo.guides.addItem(item)
//     window.Vimo.guides.clear()
//
// Estructura de un item:
// {
//   slug:    'prompts-imagen',
//   title:   'Cómo escribir prompts efectivos...',
//   excerpt: 'Aprende la estructura ideal de un prompt...',
//   thumb:   'https://...',                       // imagen hero
//   tags:    ['prompts', 'imagen'],               // ids de categoría
//   featured: false,                              // destacada con gradiente
//   url:     'guia.html?slug=prompts-imagen',     // opcional, se autogenera
// }
// ============================================================
import { qs, qsa, on, esc } from './utils.js';

const PAGE_SIZE = 9;
let _activeTag = 'all';
let _currentPage = 1;

export function initGuiasPage() {
  if (document.body.dataset.page !== 'guias') return;

  // Filtrado por tag (reinicia a página 1)
  on(document, 'click', '.guias-tag', (e, btn) => {
    qsa('.guias-tag').forEach(t => t.classList.remove('is-active'));
    btn.classList.add('is-active');
    _activeTag = btn.dataset.tag;
    _currentPage = 1;
    applyFilter();
  });

  // Paginación
  on(document, 'click', '#guiasPagination [data-page-action="prev"]', () => goToPage(_currentPage - 1));
  on(document, 'click', '#guiasPagination [data-page-action="next"]', () => goToPage(_currentPage + 1));
  on(document, 'click', '#guiasPagination [data-page]', (e, btn) => goToPage(Number(btn.dataset.page)));

  applyFilter();

  // API pública para el backend
  window.Vimo = window.Vimo || {};
  window.Vimo.guides = {
    setItems,
    addItem,
    clear,
    goToPage,
    pageSize: PAGE_SIZE,
    getItems: () => readItemsFromDOM(),
  };
}

// ── Paginación ────────────────────────────────────────────────
function goToPage(n) {
  const filtered = getFilteredCards();
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  n = Math.min(Math.max(1, n), totalPages);
  if (n === _currentPage) return;
  _currentPage = n;
  applyFilter();
  qs('#guiasGrid')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

function getFilteredCards() {
  return qsa('.guia-card', qs('#guiasGrid')).filter(card => {
    const tags = (card.dataset.tags || '').split(',').map(t => t.trim());
    return _activeTag === 'all' || tags.includes(_activeTag);
  });
}

// ── Filtrado + paginación ─────────────────────────────────────
function applyFilter() {
  const grid = qs('#guiasGrid');
  if (!grid) return;
  const all = qsa('.guia-card', grid);
  const filtered = getFilteredCards();
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  if (_currentPage > totalPages) _currentPage = totalPages;

  const start = (_currentPage - 1) * PAGE_SIZE;
  const end   = start + PAGE_SIZE;
  const pageSet = new Set(filtered.slice(start, end));

  all.forEach(card => card.classList.toggle('is-hidden', !pageSet.has(card)));

  qs('#guiasEmpty')?.classList.toggle('is-hidden', filtered.length > 0);
  renderPagination(filtered.length, totalPages);
}

// ── Render de los controles de paginación ────────────────────
function renderPagination(totalFiltered, totalPages) {
  const nav     = qs('#guiasPagination');
  const summary = qs('#guiasSummary');
  if (!nav) return;

  if (totalPages <= 1) {
    nav.classList.add('is-hidden');
    summary?.classList.add('is-hidden');
    return;
  }
  nav.classList.remove('is-hidden');

  const prev = qs('[data-page-action="prev"]', nav);
  const next = qs('[data-page-action="next"]', nav);
  if (prev) prev.disabled = _currentPage <= 1;
  if (next) next.disabled = _currentPage >= totalPages;

  qs('#guiasPaginationPages').innerHTML = buildPageButtons(_currentPage, totalPages);

  if (summary) {
    const s = (_currentPage - 1) * PAGE_SIZE + 1;
    const e = Math.min(_currentPage * PAGE_SIZE, totalFiltered);
    summary.textContent = `Mostrando ${s}–${e} de ${totalFiltered} guías`;
    summary.classList.remove('is-hidden');
  }
}

function buildPageButtons(current, total) {
  const pages = [];
  const add = (n) => pages.push(`<button class="pagination-page ${n === current ? 'is-active' : ''}" type="button" data-page="${n}">${n}</button>`);
  const dots = () => pages.push(`<span class="pagination-ellipsis">…</span>`);

  if (total <= 7) {
    for (let i = 1; i <= total; i++) add(i);
  } else {
    add(1);
    if (current > 3) dots();
    const start = Math.max(2, current - 1);
    const end   = Math.min(total - 1, current + 1);
    for (let i = start; i <= end; i++) add(i);
    if (current < total - 2) dots();
    add(total);
  }
  return pages.join('');
}

// ── Render dinámico ───────────────────────────────────────────
function setItems(items) {
  const grid = qs('#guiasGrid');
  if (!grid) return;
  grid.innerHTML = '';
  items.forEach(item => grid.appendChild(buildCard(item)));
  _currentPage = 1;
  applyFilter();
}

function addItem(item) {
  const grid = qs('#guiasGrid');
  if (!grid) return;
  grid.prepend(buildCard(item));
  applyFilter();
}

function clear() {
  qs('#guiasGrid')?.replaceChildren();
  applyFilter();
}

function readItemsFromDOM() {
  return qsa('.guia-card').map(card => ({
    slug:    card.dataset.slug,
    url:     card.getAttribute('href'),
    title:   qs('.guia-card-title', card)?.textContent.trim() || '',
    excerpt: qs('.guia-card-excerpt', card)?.textContent.trim() || '',
    tags:    (card.dataset.tags || '').split(',').filter(Boolean),
    featured: card.classList.contains('guia-card-featured'),
  }));
}

function buildCard(item) {
  const a = document.createElement('a');
  a.className = 'guia-card' + (item.featured ? ' guia-card-featured' : '');
  a.href = item.url || `guia.html?slug=${encodeURIComponent(item.slug)}`;
  a.dataset.tags = (item.tags || []).join(',');
  if (item.slug) a.dataset.slug = item.slug;

  const tagsHtml = (item.tags || [])
    .map(t => `<span class="guia-tag-chip">${esc(prettyTag(t))}</span>`)
    .join('');

  a.innerHTML = `
    <span class="guia-card-img" style="background-image:url('${esc(item.thumb || `https://picsum.photos/seed/${item.slug || 'g'}/600/400`)}')"></span>
    <span class="guia-card-body">
      <span class="guia-card-tags">${tagsHtml}</span>
      <h3 class="guia-card-title">${esc(item.title || 'Sin título')}</h3>
      <p class="guia-card-excerpt">${esc(item.excerpt || '')}</p>
      <span class="guia-card-btn ${item.featured ? 'guia-card-btn-primary' : ''}">
        Leer guía <i class="bi bi-arrow-right"></i>
      </span>
    </span>
  `;
  return a;
}

// Mapeo de ids de tag a etiquetas legibles
const TAG_LABELS = {
  prompts:   'Prompts',
  imagen:    'Imagen IA',
  video:     'Vídeo IA',
  avatares:  'Avatares',
  voz:       'Voz IA',
  marketing: 'Marketing',
  seo:       'SEO',
  plantillas:'Plantillas',
};

function prettyTag(id) {
  return TAG_LABELS[id] || id.charAt(0).toUpperCase() + id.slice(1);
}
